import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Segment } from "semantic-ui-react";
import { Formik, Form, Field } from "formik";

import * as Yup from "yup";
import * as Schemas from "../forms/validationSchemas.js";

import ValidationMessage from "../forms/messages/ValidationMessage.jsx";
import FormikSemanticInput from "../forms/formik/FormikSemanticInput.jsx";
import FormikConfirmedEmailInput from "../forms/formik/FormikConfirmedEmailInput.jsx";
import FormikConfirmedPasswordInput from "../forms/formik/FormikConfirmedPasswordInput.jsx";
import FormikActionsPanel from "../forms/formik/FormikActionsPanel.jsx";
import RegistrationFormResult from "./RegistrationFormResult.jsx";

import { selectLatestViolationErrors } from "../../state/reducers/api/apiSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class RegistrationForm extends PureComponent {
  static propTypes = {
    onReset: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isFetching: PropTypes.bool.isRequired,
    isSuccess: PropTypes.bool.isRequired,
    violationErrors: PropTypes.object,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    isFetching: false,
    copyKey: "sentry.register"
  };

  constructor(props) {
    super(props);

    this.state = {
      showMessages: false
    };

    this.initialValues = {
      firstName: "",
      lastName: "",
      email: "",
      emailConfirmation: "",
      username: "",
      password: "",
      passwordConfirmation: ""
    };
  }

  // -- VALIDATION -- //

  validationSchema = function () {
    const messages = this.props.copy("forms.validation_requirements");

    return Yup.object().shape({
      firstName: Schemas.simpleStringSchema("First Name", messages, true),
      lastName: Schemas.simpleStringSchema("Last Name", messages, true),
      username: Schemas.usernameSchema("Username", messages, true),
      email: Schemas.emailSchema("Email", messages, true),
      emailConfirmation: Schemas.emailConfirmationSchema(
        "Confirm Email",
        messages,
        true
      ),
      password: Schemas.passwordSchema("Password", messages, true),
      passwordConfirmation: Schemas.passwordConfirmationSchema(
        "Confirm Password",
        messages,
        true
      )
    });
  };

  // -- HANDLERS -- //

  // triggered whenever the reset button is clicked
  onReset = () => {
    this.setState({ showMessages: false });
  };

  onSubmit = () => {
    this.setState({ showMessages: true });
  };

  // only triggered whenever the submit button is clicked and the form is valid
  onFormikSubmit = values => {
    this.props.onSubmit(values);
  };

  // -- RENDER -- //

  // handling this outside of Formik as the message should be shown as soon as submit has been clicked
  // not just on successfull Formik submission, or on failed validation check.
  renderMessage = formikProps => {
    if (this.state.showMessages !== true) return null;

    const valid =
      formikProps.isValid && !this.props.violationErrors ? true : false;

    return <ValidationMessage className="mid-panel-message" valid={valid} />;
  };

  renderTextInput = (name, copyKey) => {
    const { isFetching, violationErrors, copy } = this.props;

    return (
      <Field
        name={name}
        placeholder={copy(`${copyKey}_input_placeholder`)}
        label={copy(`${copyKey}_input_label`)}
        required={true}
        fluid
        disabled={isFetching}
        apiErrors={violationErrors}
        component={FormikSemanticInput}
      />
    );
  };

  render() {
    const { isSuccess, violationErrors, copyKey, copy } = this.props;

    if (isSuccess) {
      return (
        <RegistrationFormResult
          onContinue={this.onContinue}
          isSuccess={isSuccess}
        />
      );
    }

    return (
      <Formik
        initialValues={this.initialValues} // required by semantic-ui react
        validateOnMount={true}
        enableReinitialize={true}
        validationSchema={this.validationSchema()}
        onSubmit={this.onFormikSubmit}
      >
        {formikProps => {
          return (
            <Segment padded textAlign="left">
              <Form className="ui form">
                {this.renderMessage(formikProps)}

                {this.renderTextInput("firstName", `${copyKey}.firstname`)}
                {this.renderTextInput("lastName", `${copyKey}.lastname`)}
                {this.renderTextInput("username", `${copyKey}.username`)}

                <Segment>
                  <FormikConfirmedEmailInput apiErrors={violationErrors} />
                </Segment>
                <Segment>
                  <FormikConfirmedPasswordInput apiErrors={violationErrors} />
                </Segment>

                <FormikActionsPanel
                  reset={true}
                  onReset={this.onReset}
                  submit={true}
                  onSubmit={this.onSubmit}
                  panelCopy={copy(`${copyKey}.actions_panel`)}
                />
              </Form>
            </Segment>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps = state => ({
  isFetching: state.signup.isFetching,
  isSuccess: state.signup.isSuccess,
  violationErrors: selectLatestViolationErrors(state),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(RegistrationForm);
